import type { FC, ReactElement } from "react"

import type { QueryKey } from "@tanstack/react-query"
import { AnimatePresence, m } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import { Trans } from "react-i18next"

import { ErrorIcon } from "@/3514/assets"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { THEME } from "@/3514/theme"
import { type ICoachingSessionModel, IUserParticipantModel } from "@/3514/types"
import { withPressHandler } from "@/3514/utils"

import classes from "../../styles"
import type { TCoachingSessionsScheduleAvailableModalProps as TScheduleAvailableModalProps } from "../../types"

type _TProps = {
    sessions: ICoachingSessionModel[]
    sessionsQueryKey: QueryKey
    user: IUserParticipantModel
    participantId: number
}
const CoachingSessionsScheduleAvailableModalOpener: FC<_TProps> = ({
    sessions,
    sessionsQueryKey,
    user,
    participantId
}: _TProps): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    function handleAction(): void {
        modalDispatcher.showModal<TScheduleAvailableModalProps>(EModalId.ScheduleAvailableSessions, {
            sessions,
            sessionsQueryKey,
            user,
            participantId
        })
    }

    return (
        <AnimatePresence initial mode="wait" presenceAffectsLayout>
            {!isEmpty(sessions) && (
                <m.button
                    type="button"
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.3, type: "tween", ease: "easeOut" }}
                    className={classes.modal.scheduleAvailable.opener.container()}
                    aria-label="Interact to open scheduleble sessions modal"
                    onClick={withPressHandler(handleAction)}
                    onKeyDown={withPressHandler(handleAction)}
                    tabIndex={0}
                >
                    <ErrorIcon
                        fillColor={THEME.COLORS.red[200]}
                        className={classes.modal.scheduleAvailable.opener.icon()}
                    />

                    <Trans
                        parent="p"
                        i18nKey="entities.sessions.unscheduledSessions.message"
                        count={sessions.length}
                        className={classes.modal.scheduleAvailable.opener.text()}
                        components={{
                            strong: (
                                <strong
                                    key="entities.sessions.unscheduledSessions.message-1"
                                    className={classes.modal.scheduleAvailable.opener.textStrong()}
                                />
                            ),
                            br:
                                breakpoints.xs || breakpoints.sm ? (
                                    <br key="entities.sessions.unscheduledSessions.message-2" />
                                ) : (
                                    <span key="entities.sessions.unscheduledSessions.message-2" />
                                )
                        }}
                    />
                </m.button>
            )}
        </AnimatePresence>
    )
}

CoachingSessionsScheduleAvailableModalOpener.displayName = "CoachingSessionsScheduleAvailableModalOpener"

export default CoachingSessionsScheduleAvailableModalOpener
